import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { MenuMainElementRefContext } from 'modules/menu/MenuMainElementRefContext';

type Props = {
  children?: React.ReactNode | React.ReactNode[];
};

const Footer = styled.div<{ sticky: boolean }>`
  position: -webkit-sticky; /* stylelint-disable-line */
  position: sticky;
  width: 100%;
  bottom: -1px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  padding: 24px;
  padding-inline-end: 64px;
  z-index: 1;

  ${({ sticky }) =>
    sticky &&
    css`
      padding-inline-end: 88px; /* add 24px of extra padding to accommodate for the Intercom bubble */
      width: calc(100% + 48px); /* Make up for the 24px paddings on left & right to get a full width element */
      margin-inline-start: -24px; /* Accommodate for the "extra" width added */
    `}
`;

const StickyFooter = ({ children }: Props) => {
  const [isSticky, setIsSticky] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuMainElementRef = useContext(MenuMainElementRefContext);

  useEffect(() => {
    // Old browsers don't support IntersectionObserver
    if (!('IntersectionObserver' in window)) {
      return;
    }
    if (menuMainElementRef && menuMainElementRef.current) {
      const cachedRef = ref.current;

      if (cachedRef) {
        const observer = new IntersectionObserver(([e]) => setIsSticky(e.intersectionRatio < 1), {
          root: menuMainElementRef.current,
          threshold: [1],
        });

        observer.observe(cachedRef);

        return () => observer.unobserve(cachedRef);
      }
    }
    return;
  }, [menuMainElementRef, ref]);

  return (
    <Footer ref={ref} sticky={isSticky}>
      {children}
    </Footer>
  );
};

export default StickyFooter;
